import { useLayoutEffect, useRef, useState } from 'react';
import AVIcon from '../../components/icons/AllVoicedIcons';
import './SelectedParticipantExperiences.css';

const PUBLIC_BYLINE_FIELDS = new Set([
  'full_name',
  'profession',
  'professional_roles',
  'affiliation',
  'country',
]);

export function ExperienceByline({ identity = [] }) {
  const authorized = Object.fromEntries(
    identity
      .filter(
        ({ field, value }) =>
          PUBLIC_BYLINE_FIELDS.has(field) &&
          String(value || '').trim()
      )
      .map(({ field, value }) => [field, value])
  );

  const supporting = [
    authorized.professional_roles,
    authorized.affiliation,
    authorized.country,
  ].filter(Boolean);

  if (
    !authorized.full_name &&
    !authorized.profession &&
    !supporting.length
  ) {
    return null;
  }

  return (
    <div
      aria-label="Participant public identity"
      className="selected-experience__byline"
    >
      {authorized.full_name && (
        <p className="selected-experience__name">
          {authorized.full_name}
        </p>
      )}

      {authorized.profession && (
        <p className="selected-experience__profession">
          {authorized.profession}
        </p>
      )}

      {!!supporting.length && (
        <p className="selected-experience__metadata">
          {supporting.map((value, index) => (
            <span key={`${index}:${value}`}>
              {index > 0 && (
                <span
                  aria-hidden="true"
                  className="selected-experience__separator"
                >
                  {' · '}
                </span>
              )}
              {value}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

export default function ParticipantExperienceCard({
  identity = [],
  text,
  collapsible = false,
}) {
  const textRef = useRef(null);

  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const isClamped = collapsible && !expanded;

  useLayoutEffect(() => {
    if (!collapsible || !textRef.current || expanded) {
      return undefined;
    }

    const element = textRef.current;

    const measureOverflow = () => {
      setIsOverflowing(
        element.scrollHeight > element.clientHeight + 1
      );
    };

    measureOverflow();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measureOverflow);
      observer.observe(element);

      return () => {
        observer.disconnect();
      };
    }

    window.addEventListener('resize', measureOverflow);

    return () => {
      window.removeEventListener('resize', measureOverflow);
    };
  }, [collapsible, expanded, text]);

  return (
    <figure className="selected-experience">
      <AVIcon
        aria-hidden="true"
        className="selected-experience__quote-icon"
        focusable="false"
        icon="quoteLeft"
        size={30}
      />

      <blockquote
        className={[
          'selected-experience__text',
          isClamped
            ? 'selected-experience__text--clamped'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={textRef}
      >
        {text}
      </blockquote>

      {collapsible && isOverflowing && (
        <button
          aria-expanded={expanded}
          className="selected-experience__toggle"
          onClick={() => setExpanded((current) => !current)}
          type="button"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}

      <figcaption>
        <ExperienceByline identity={identity} />
      </figcaption>
    </figure>
  );
}
